// ./src/styles/theme/index.ts
// ============================================
// FormSubmits Main Theme Configuration
// Aligned with landing website design system
// ============================================

import { createTheme } from "@mantine/core";
import {
  brand,
  accent,
  success,
  error,
  warning,
  neutral,
  gradients,
  backgrounds,
  textColors,
  borderColors,
} from "./colors";
import { fonts } from "./fonts";

export const formSubmitsTheme = createTheme({
  // ============================================
  // Color Configuration
  // ============================================
  colors: {
    brand,
    accent,
    success,
    error,
    warning,
    neutral,
  },

  // Primary color reference
  primaryColor: "brand",
  primaryShade: { light: 5, dark: 7 },

  // ============================================
  // Typography
  // ============================================
  fontFamily: fonts.primary,
  fontFamilyMonospace: fonts.monospace,

  headings: {
    fontFamily: fonts.display,
    fontWeight: "700",
    sizes: {
      h1: { fontSize: fonts.sizes.h1, lineHeight: "1.1", fontWeight: "800" },
      h2: { fontSize: fonts.sizes.h2, lineHeight: "1.2", fontWeight: "700" },
      h3: { fontSize: fonts.sizes.h3, lineHeight: "1.3", fontWeight: "600" },
      h4: { fontSize: fonts.sizes.h4, lineHeight: "1.4", fontWeight: "600" },
      h5: { fontSize: fonts.sizes.h5, lineHeight: "1.5", fontWeight: "600" },
      h6: { fontSize: fonts.sizes.h6, lineHeight: "1.5", fontWeight: "600" },
    },
  },

  // ============================================
  // Spacing
  // ============================================
  spacing: {
    xs: "0.5rem", // 8px
    sm: "0.75rem", // 12px
    md: "1rem", // 16px
    lg: "1.5rem", // 24px
    xl: "2rem", // 32px
  },

  // ============================================
  // Border Radius
  // ============================================
  radius: {
    xs: "0.25rem", // 4px
    sm: "0.375rem", // 6px
    md: "0.5rem", // 8px
    lg: "0.75rem", // 12px
    xl: "1rem", // 16px
  },

  // ============================================
  // Shadows
  // ============================================
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
  },

  // ============================================
  // Default Settings
  // ============================================
  defaultRadius: "md",
  cursorType: "pointer",
  respectReducedMotion: true,

  // ============================================
  // Custom Properties
  // ============================================
  other: {
    // Brand colors (quick access)
    brandPrimary: "#6c5ce7",
    brandDark: "#271f5c",
    brandLight: "#f3f1ff",
    accentCyan: "#00d4e8",

    // Semantic colors
    successGreen: "#10b981",
    errorRed: "#ef4444",
    warningAmber: "#f59e0b",

    // Background variations
    backgrounds,

    // Text colors
    text: textColors,

    // Border colors
    borders: borderColors,

    // Gradients
    gradients,

    // Brand shadows (for CTAs and cards)
    shadowBrand: "0 4px 14px 0 rgb(108 92 231 / 0.25)",
    shadowBrandLg: "0 10px 40px 0 rgb(108 92 231 / 0.3)",

    // Animation durations
    animations: {
      fast: "150ms",
      normal: "200ms",
      slow: "300ms",
    },

    // Z-index scale
    zIndex: {
      dropdown: 1000,
      sticky: 1020,
      fixed: 1030,
      modal: 1040,
      popover: 1050,
      tooltip: 1060,
      notification: 1070,
    },
  },
});

// ============================================
// Re-exports for convenience
// ============================================
export { fonts, textStyles } from "./fonts";
export {
  brand,
  accent,
  success,
  error,
  warning,
  neutral,
  semanticColors,
  gradients,
  backgrounds,
  textColors,
  borderColors,
  colorPalette,
} from "./colors";

export default formSubmitsTheme;
